/**
 * imageUploadService.ts
 *
 * Service for batch image uploads to custom API endpoints.
 * Groups images by endpoint and uploads them together for efficiency.
 */

import { CustomApiConfig } from '../types/types';

export interface ImageField {
  fieldId: string;
  file: File;
  endpoint: string; // customApi.url
  config: CustomApiConfig;
}

interface UploadGroup {
  endpoint: string;
  config: CustomApiConfig;
  images: Array<{ fieldId: string; file: File }>;
}

/**
 * Upload images to their configured endpoints.
 * Groups images by endpoint and uploads them in parallel.
 * Returns a map of fieldId -> fileUrl/fileId from the endpoint responses.
 */
export async function uploadImages(
  images: ImageField[],
  getAuthHeaders: () => Promise<Record<string, string>>
): Promise<Record<string, string>> {
  // Validate all images have customApi configured
  const unconfigured = images.filter(img => !img.endpoint || !img.config?.url);
  if (unconfigured.length > 0) {
    const fieldIds = unconfigured.map(i => i.fieldId).join(', ');
    throw new Error(
      `Images without upload endpoint configured: ${fieldIds}`
    );
  }

  // Group images by endpoint
  const grouped = groupImagesByEndpoint(images);

  // Upload all groups in parallel, collect responses
  const responses = await Promise.all(
    grouped.map(group => uploadImageGroup(group, getAuthHeaders))
  );

  // Merge all responses into single object: { fieldId: url, ... }
  return mergeResponses(responses);
}

/**
 * Group images by their endpoint URL
 */
function groupImagesByEndpoint(images: ImageField[]): UploadGroup[] {
  const groups: Map<string, UploadGroup> = new Map();

  images.forEach(image => {
    const key = image.endpoint;
    if (!groups.has(key)) {
      groups.set(key, {
        endpoint: image.endpoint,
        config: image.config,
        images: [],
      });
    }
    groups.get(key)!.images.push({
      fieldId: image.fieldId,
      file: image.file,
    });
  });

  return Array.from(groups.values());
}

/**
 * Upload a group of images to a single endpoint
 */
async function uploadImageGroup(
  group: UploadGroup,
  getAuthHeaders: () => Promise<Record<string, string>>
): Promise<Record<string, string>> {
  const formData = new FormData();

  // Add each image with field ID as key
  group.images.forEach(({ fieldId, file }) => {
    formData.append(fieldId, file);
  });

  // Add query parameters to FormData if configured
  if (group.config.queryParams) {
    Object.entries(group.config.queryParams).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  // Get auth headers
  const authHeaders = await getAuthHeaders();

  // Merge custom headers with auth headers
  const requestHeaders = {
    ...authHeaders,
    ...(group.config.headers || {}),
  };

  // Don't set Content-Type header for FormData - let browser set it with boundary
  delete requestHeaders['Content-Type'];

  try {
    // Make the request - ALWAYS use POST for image uploads
    const response = await fetch(group.config.url, {
      method: 'POST',
      headers: requestHeaders,
      body: formData,
    });

    if (!response.ok) {
      throw new Error(
        `Upload failed: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    // Validate response has required code field
    if (data.code !== 200) {
      throw new Error(
        `Upload error: ${data.message || 'Unknown error from server'}`
      );
    }

    // Extract data from response path (e.g., "data" means response.data)
    const responseData = group.config.responsePath
      ? getValueByPath(data, group.config.responsePath)
      : data;

    if (!responseData || typeof responseData !== 'object') {
      throw new Error(
        'Invalid response format from image upload endpoint. Expected object with field IDs as keys.'
      );
    }

    return responseData as Record<string, string>;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Failed to upload to ${group.config.url}: ${message}`);
  }
}

/**
 * Merge multiple response objects into one
 */
function mergeResponses(responses: Record<string, string>[]): Record<string, string> {
  return responses.reduce((acc, curr) => ({ ...acc, ...curr }), {});
}

/**
 * Get value from object using dot notation path
 * Example: getValueByPath({data: {items: [1,2,3]}}, 'data.items') => [1,2,3]
 */
function getValueByPath(obj: any, path: string): any {
  return path.split('.').reduce((current, part) => current?.[part], obj);
}

/**
 * Validate image file before upload
 */
export function validateImageFile(
  file: File,
  maxFileSize?: number,
  acceptedFormats?: string[]
): { valid: boolean; error?: string } {
  // Check file type
  if (acceptedFormats && acceptedFormats.length > 0) {
    if (!acceptedFormats.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file format. Accepted: ${acceptedFormats.join(', ')}`,
      };
    }
  } else {
    // Default: check if it's an image
    if (!file.type.startsWith('image/')) {
      return {
        valid: false,
        error: 'Only image files are allowed',
      };
    }
  }

  // Check file size
  if (maxFileSize) {
    const maxBytes = maxFileSize * 1024 * 1024;
    if (file.size > maxBytes) {
      return {
        valid: false,
        error: `File too large. Maximum: ${maxFileSize}MB`,
      };
    }
  }

  return { valid: true };
}
